import type { Lang } from './i18n';

export function getDateLocale(lang: Lang): string {
  return lang === 'cs' ? 'cs-CZ' : 'en-GB';
}

function coerceDate(value: Date | string | number): Date {
  return value instanceof Date ? value : new Date(value);
}

export function formatDate(
  value: Date | string | number,
  lang: Lang,
  options?: Intl.DateTimeFormatOptions,
): string {
  const date = coerceDate(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(getDateLocale(lang), options);
}

export function formatDateTime(
  value: Date | string | number,
  lang: Lang,
  options?: Intl.DateTimeFormatOptions,
): string {
  const date = coerceDate(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(getDateLocale(lang), options);
}

/** API date or datetime -> yyyy-MM-dd for <input type="date"> */
export function toDateInputValue(value: string | null | undefined): string {
  if (!value) return '';
  return value.split('T')[0];
}

/** Date-only string (yyyy-MM-dd) without UTC timezone shift */
export function formatDateOnly(value: string, lang: Lang): string {
  const part = value.split('T')[0];
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(part);
  if (!match) return value;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return formatDate(date, lang);
}

export interface DateInputParts {
  day: string;
  month: string;
  year: string;
}

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** yyyy-MM-dd -> { day, month, year } segment strings */
export function splitDateInputValue(value: string | null | undefined): DateInputParts {
  const part = (value ?? '').split('T')[0];
  const match = ISO_DATE_RE.exec(part);
  if (!match) return { day: '', month: '', year: '' };
  return { year: match[1], month: match[2], day: match[3] };
}

const MIN_BIRTH_YEAR = 1900;

export function isValidDateParts(day: string, month: string, year: string): boolean {
  if (!/^\d{2}$/.test(day) || !/^\d{2}$/.test(month) || !/^\d{4}$/.test(year)) return false;
  const d = Number(day);
  const m = Number(month);
  const y = Number(year);
  if (d < 1 || d > 31 || m < 1 || m > 12 || y < MIN_BIRTH_YEAR || y > new Date().getFullYear()) return false;
  const date = new Date(y, m - 1, d);
  return date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d;
}

/** Segment strings -> yyyy-MM-dd when complete and valid, otherwise '' */
export function composeDateInputValue(day: string, month: string, year: string): string {
  if (!isValidDateParts(day, month, year)) return '';
  return `${year}-${month}-${day}`;
}

/** Parse pasted text (ISO or DD.MM.YYYY) into segment strings */
export function parsePastedDate(text: string): DateInputParts | null {
  const trimmed = text.trim();
  const iso = ISO_DATE_RE.exec(trimmed);
  if (iso) return { year: iso[1], month: iso[2], day: iso[3] };
  const dotted = /^(\d{1,2})[./](\d{1,2})[./](\d{4})$/.exec(trimmed);
  if (dotted) {
    return {
      day: dotted[1].padStart(2, '0'),
      month: dotted[2].padStart(2, '0'),
      year: dotted[3],
    };
  }
  return null;
}
